import React from 'react'
import { useHistory } from 'react-router'
import { useAppSelector } from '../../../../../hooks/redux'
import { Row, createColumnHelper } from '@tanstack/react-table'

import BodyWidget from '../../../../../components/atoms/BodyWidget'
import Table from '../../../../../components/molecules/Table'
import Client from '../../../../../components/molecules/Table/Cells/Client'
import LatestActivity from './LatestActivity'
import Age from './Age'

import { Routes } from '../../../../../Routes'
import { getLatestEvent } from '../../../../../utilities'
import { selectConvo } from '../../../../../slices/convo'
import useAccess, { PERMS } from '../../../../../hooks/useAccess'
import { useUserAPI } from '../../../../../providers/UserAPIProvider'
import { useBreakpoint } from '../../../../../providers/BreakpointProvider'
import { useEnvironment } from '../../../../../providers/EnvironmentProvider'
import useStyles from './styles'

const columnHelper = createColumnHelper<LocalSession>()

const OpenConversations = () => {
  const history = useHistory()

  const { allConversations, allConversationsLoaded } =
    useAppSelector(selectConvo)

  const { allUsers, loadUser } = useUserAPI()
  const hasPermission = useAccess()
  const { user } = useEnvironment()
  const { down480, between480_768 } = useBreakpoint()

  // Get list of open conversations
  const openConvos = React.useMemo(() => {
    const sortedOpen = Object.values(allConversations)
      .filter(convo => convo.owner_id && !convo.ended_at)
      .sort((a, b) => +new Date(b.lastUpdate) - +new Date(a.lastUpdate))

    if (
      hasPermission(PERMS.ConversationsBasic) &&
      hasPermission(PERMS.ConversationsActive)
    ) {
      return sortedOpen.filter(
        convo => convo.assigned_to === user?.id || !convo.assigned_to
      )
    }

    if (hasPermission(PERMS.ConversationsBasic)) {
      return sortedOpen.filter(convo => convo.assigned_to === user?.id)
    }

    return sortedOpen
  }, [allConversations, hasPermission, user])

  // Loads owners
  React.useEffect(() => {
    openConvos.forEach(convo => loadUser(convo.owner_id))
  }, [openConvos, loadUser])

  const classes = useStyles()

  const handleRowClicked = React.useCallback(
    (row: Row<LocalSession>) => {
      const { session } = row.original
      history.replace(`${Routes.CLIENT_CHATS}?session=${session}`)
    },
    [history]
  )

  const columns = React.useMemo(() => {
    const nameCol = columnHelper.accessor('owner_id', {
      header: 'Name',
      cell: ({ row }) => (
        <Client
          client={
            allUsers[row.original.owner_id] ?? {
              id: row.original.owner_id,
              name: 'Loading...'
            }
          }
          onClick={() => handleRowClicked(row)}
        />
      )
    })

    const activityCol = columnHelper.accessor('latest_activity', {
      header: 'Last Activity',
      cell: ({ row }) => (
        <LatestActivity
          convo={row.original}
          activity={getLatestEvent(row.original, row.original.session)}
        />
      )
    })

    const ageCol = columnHelper.accessor('createdAt', {
      header: 'Age',
      cell: ({ row }) => <Age date={new Date(row.original.createdAt)} />
    })

    if (down480) {
      return [nameCol]
    }

    if (between480_768) {
      return [nameCol, ageCol]
    }

    return [nameCol, activityCol, ageCol]
  }, [allUsers, down480, between480_768, handleRowClicked])

  return (
    <BodyWidget title='Open Conversations' className={classes.table}>
      <Table
        id='open-conversations'
        data={openConvos}
        columns={columns}
        onRowClicked={handleRowClicked}
        loaded={allConversationsLoaded}
        message='You have no open conversations'
        hideCount
        disablePagination
        disableSkeleton
      />
    </BodyWidget>
  )
}

export default OpenConversations
